/**
 * Composable for support actions (LiveChat or fallback redirect)
 * Keeps the logic centralized and reusable across Alpine magics/components.
 */
export const useSupport = () => {
    /**
     * Safely resolve a support URL relative to current origin when needed
     * Accepts absolute or relative paths and ensures a proper URL string.
     */
    const resolveSupportUrl = (path) => {
        try {
            // If path is absolute, URL will keep it; if relative, it will resolve against current origin
            const url = new URL(path, window.location.origin);
            return url.toString();
        } catch (e) {
            console.error("Invalid supportPath provided:", path, e);
            return null;
        }
    };

    /**
     * Open support: either maximize LiveChat or navigate to support page.
     */
    const open = () => {
        try {
            const enabled = !!(window.appContext?.Config?.livechat?.enabled);
            const supportPath = window.appContext?.Config?.livechat?.supportPath;

            switch (true) {
                case enabled && typeof LiveChatWidget !== "undefined":
                    LiveChatWidget.call("maximize");
                    break;

                case !!supportPath: {
                    const url = resolveSupportUrl(supportPath);
                    if (url) {
                        // Use assign so users can navigate back, and ensure we remain on resolved URL
                        window.location.assign(url);
                    } else {
                        console.error("Support path not configured correctly");
                    }
                    break;
                }

                default:
                    console.error("LiveChatWidget is not available and no support path configured");
            }
        } catch (e) {
            console.error("Failed to open support:", e);
        }
    };

    return { open };
};

/**
 * Immediately unlock support triggers when LiveChat is disabled.
 * If chat is disabled, the widget never loads, so we must remove the
 * 'live-chat-disabled' class right away to allow instant redirection.
 */
const _unlockDisabledChatTriggers = () => {
    try {
        const enabled = !!(window.appContext?.Config?.livechat?.enabled);
        if (!enabled) {
            const nodes = document.getElementsByClassName("live-chat-disabled");
            if (nodes && nodes.length) {
                Array.from(nodes).forEach((el) => el.classList.remove("live-chat-disabled"));
            }
        }
    } catch (e) {
        console.error("Failed to unlock support triggers for disabled chat:", e);
    }
};

// Run as soon as the DOM is ready (or immediately if it's already ready)
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", _unlockDisabledChatTriggers, { once: true });
} else {
    _unlockDisabledChatTriggers();
}
